import { useEffect, useRef, useState, type ReactNode } from "react";

import { api, ApiError, errorMessage } from "../api/client";
import { ui } from "../lib/ui";

/** Shared scaffolding for drawing over locally-opened footage: file opening,
 *  the media element with its NVR-decode quirks, play/scrub controls, frame
 *  size detection and screen↔frame-pixel conversion.
 *
 *  A file the browser can decode never leaves the machine (object URL). A
 *  native NVR export (.dav, .264, …) cannot be decoded by any browser, so
 *  it — or, being a raw stream, just its first megabytes — is sent to the
 *  API host, which converts its first minute with the same ffmpeg the
 *  pipeline preprocesses with and keeps nothing (`POST /footage/preview`).
 *  The same route is offered when a file the browser should play fails to
 *  decode (an HEVC MP4, say). */

/** Raw-stream exports: no container, so no browser decodes them — and a
 *  prefix of the file is a prefix of the video. Mirrors
 *  `footfall.ingestion.preprocess.NATIVE_EXTS`. */
const NATIVE_EXTS = [".dav", ".h264", ".264", ".h265", ".265", ".hevc"];
/** How much of a native export to send: many seconds of even a 4K stream,
 *  and far more than the minute the server returns. */
const NATIVE_UPLOAD_BYTES = 64 * 1024 * 1024;
/** The server's own limit for a whole file (a container needs its index). */
const MAX_UPLOAD_BYTES = 256 * 1024 * 1024;

const MANUAL_HINT =
  "Convert it by hand instead (ffmpeg -i clip.dav -c:v libx264 -an clip.mp4) " +
  "or open a screenshot of the frame.";

function isNativeExport(name: string): boolean {
  const dot = name.lastIndexOf(".");
  return dot >= 0 && NATIVE_EXTS.includes(name.slice(dot).toLowerCase());
}

/** Whether a prefix of the file is a prefix of the video, so only the first
 *  megabytes need sending. True for the raw exports by extension, and — by
 *  looking at the bytes, since the NVR names it `.mp4` regardless — for an
 *  MPEG program stream, which no browser plays either: Hikvision's classic
 *  export opens with its private `IMKH` header (the first pack header,
 *  `00 00 01 BA`, follows a few KiB in), a plain one with the pack header
 *  itself. A real MP4 (`ftyp` at byte 4) needs its index and goes whole,
 *  as does anything unrecognised. */
async function isPrefixSafe(file: File): Promise<boolean> {
  if (isNativeExport(file.name)) return true;
  const head = new Uint8Array(await file.slice(0, 8).arrayBuffer());
  if (head.length < 8) return false;
  const ascii = (from: number, to: number) => String.fromCharCode(...head.subarray(from, to));
  if (ascii(4, 8) === "ftyp") return false;
  return (
    ascii(0, 4) === "IMKH" ||
    (head[0] === 0 && head[1] === 0 && head[2] === 1 && head[3] === 0xba)
  );
}

function mib(bytes: number): string {
  return `${Math.round(bytes / (1024 * 1024))} MiB`;
}

export interface FrameSize {
  width: number;
  height: number;
}

export interface MediaFrameContext {
  frame: FrameSize;
  isImage: boolean;
  /** Frame pixels per screen pixel — keeps overlay handles/labels a constant
   *  on-screen size. */
  k: number;
  /** Screen (client) coordinates → native frame pixels, clamped and rounded. */
  toFrame: (clientX: number, clientY: number) => [number, number] | null;
  /** Native frame pixels → screen (client) coordinates. */
  toScreen: (p: [number, number]) => [number, number] | null;
  /** The decoded element — e.g. for canvas cropping. */
  source: HTMLVideoElement | HTMLImageElement | null;
  pause: () => void;
}

interface MediaFrameViewerProps {
  openLabel: string;
  changeLabel: string;
  /** Shown in the dashed placeholder before a file is opened. */
  emptyHint: ReactNode;
  /** Extra toolbar controls, right-aligned; ctx is null until a file decodes. */
  actions?: (ctx: MediaFrameContext | null) => ReactNode;
  /** A new file was chosen (before it decodes). */
  onOpen?: () => void;
  /** A file decoded and reported its pixel size. */
  onFrame?: (size: FrameSize, isImage: boolean) => void;
  /** Video playback started. */
  onPlay?: () => void;
  /** The tool's overlay — an absolutely-positioned SVG over the media. */
  overlay: (ctx: MediaFrameContext) => ReactNode;
}

export function MediaFrameViewer({
  openLabel,
  changeLabel,
  emptyHint,
  actions,
  onOpen,
  onFrame,
  onPlay,
  overlay,
}: MediaFrameViewerProps) {
  const [fileName, setFileName] = useState<string | null>(null);
  const [mediaUrl, setMediaUrl] = useState<string | null>(null);
  const [isImage, setIsImage] = useState(false);
  const [frame, setFrame] = useState<FrameSize | null>(null);
  const [duration, setDuration] = useState(0);
  const [time, setTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [mediaError, setMediaError] = useState<string | null>(null);
  // The file as opened, kept so a decode failure can still offer the
  // server conversion; `converted` names it once the media is the server's
  // MP4 rather than the file itself.
  const [sourceFile, setSourceFile] = useState<File | null>(null);
  const [converting, setConverting] = useState(false);
  const [converted, setConverted] = useState<string | null>(null);
  // Bumped per opened file, so a conversion finishing for an earlier file
  // is dropped rather than replacing the current one.
  const openSeq = useRef(0);

  // Frame pixels per screen pixel, tracked off the media container's box.
  const [k, setK] = useState(1);

  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !frame) return;
    const update = () => {
      const w = el.getBoundingClientRect().width;
      if (w > 0) setK(frame.width / w);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [frame]);

  // Release the object URL when the file changes or the tool closes.
  useEffect(() => {
    return () => {
      if (mediaUrl) URL.revokeObjectURL(mediaUrl);
    };
  }, [mediaUrl]);

  /** Send the file (a native export: its first bytes) to the API host and
   *  play the MP4 it makes. */
  const convertOnServer = async (file: File) => {
    const seq = openSeq.current;
    const native = await isPrefixSafe(file);
    if (seq !== openSeq.current) return;
    if (!native && file.size > MAX_UPLOAD_BYTES) {
      setMediaError(
        `This ${mib(file.size)} file is too large to convert here (the limit is ` +
          `${mib(MAX_UPLOAD_BYTES)}). Trim it to a short clip first, or ${MANUAL_HINT}`,
      );
      return;
    }
    setConverting(true);
    setMediaError(null);
    try {
      const clip = await api.previewFootage(
        native ? file.slice(0, NATIVE_UPLOAD_BYTES) : file,
        file.name,
      );
      if (seq !== openSeq.current) return;
      setConverted(file.name);
      setMediaUrl(URL.createObjectURL(clip));
    } catch (err) {
      if (seq !== openSeq.current) return;
      const detail = errorMessage(err);
      setMediaError(
        err instanceof ApiError && err.status === 503
          ? `${detail}. ${MANUAL_HINT}`
          : `Could not convert this file: ${detail}`,
      );
    } finally {
      if (seq === openSeq.current) setConverting(false);
    }
  };

  const openFile = (file: File | null) => {
    if (!file) return;
    openSeq.current += 1;
    setFileName(file.name);
    setSourceFile(file);
    setConverted(null);
    setConverting(false);
    setIsImage(file.type.startsWith("image/"));
    setFrame(null);
    setDuration(0);
    setTime(0);
    setPlaying(false);
    setMediaError(null);
    onOpen?.();
    setMediaUrl(null);
    const seq = openSeq.current;
    void (async () => {
      // No browser decodes a raw NVR stream, or a program stream hiding
      // behind an .mp4 name; those skip straight to the server. Anything
      // else is tried locally first (an object URL — nothing leaves the PC).
      const stream = !file.type.startsWith("image/") && (await isPrefixSafe(file));
      if (seq !== openSeq.current) return;
      if (stream) void convertOnServer(file);
      else setMediaUrl(URL.createObjectURL(file));
    })();
  };

  const adopt = (size: FrameSize, image: boolean) => {
    setFrame(size);
    onFrame?.(size, image);
  };

  const toFrame = (clientX: number, clientY: number): [number, number] | null => {
    const el = containerRef.current;
    if (!el || !frame) return null;
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;
    return [
      Math.min(
        Math.max(Math.round(((clientX - rect.left) / rect.width) * frame.width), 0),
        frame.width,
      ),
      Math.min(
        Math.max(Math.round(((clientY - rect.top) / rect.height) * frame.height), 0),
        frame.height,
      ),
    ];
  };

  const toScreen = (p: [number, number]): [number, number] | null => {
    const el = containerRef.current;
    if (!el || !frame) return null;
    const rect = el.getBoundingClientRect();
    return [
      rect.left + (p[0] / frame.width) * rect.width,
      rect.top + (p[1] / frame.height) * rect.height,
    ];
  };

  const pause = () => {
    const v = videoRef.current;
    if (v && !v.paused) {
      v.pause();
      setPlaying(false);
    }
  };

  const seek = (t: number) => {
    const v = videoRef.current;
    setTime(t);
    if (v) v.currentTime = t;
  };

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      void v.play();
      setPlaying(true);
      onPlay?.();
    } else {
      pause();
    }
  };

  const ctx: MediaFrameContext | null = frame
    ? {
        frame,
        isImage,
        k,
        toFrame,
        toScreen,
        source: isImage ? imageRef.current : videoRef.current,
        pause,
      }
    : null;

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex flex-wrap items-center gap-2.5">
        <label className={`${ui.btnQuiet} relative overflow-hidden`}>
          {fileName ? changeLabel : openLabel}
          <input
            type="file"
            // Native exports have no MIME type, so the picker needs them by name.
            accept={`video/*,image/*,${NATIVE_EXTS.join(",")}`}
            className="absolute inset-0 cursor-pointer opacity-0"
            onChange={(e) => openFile(e.target.files?.[0] ?? null)}
          />
        </label>
        {fileName && (
          <span
            className="max-w-[320px] truncate font-mono text-xs text-muted"
            title={fileName}
          >
            {fileName}
            {frame && ` · ${frame.width}×${frame.height}`}
          </span>
        )}
        {converting && (
          <span className="text-xs text-muted">Converting with ffmpeg on the server…</span>
        )}
        {!isImage && frame && (
          <button type="button" className={ui.btnQuiet} onClick={togglePlay}>
            {playing ? "Pause" : "Play"}
          </button>
        )}
        {!isImage && duration > 0 && (
          <>
            <input
              type="range"
              className="min-w-[120px] flex-[1_1_160px]"
              min={0}
              max={duration}
              step={0.05}
              value={time}
              onChange={(e) => seek(Number(e.target.value))}
              aria-label="Scrub video"
            />
            <span className="font-mono text-xs text-muted">{time.toFixed(1)}s</span>
          </>
        )}
        {actions && (
          <>
            <span className="grow" />
            {actions(ctx)}
          </>
        )}
      </div>

      {mediaUrl ? (
        <div
          ref={containerRef}
          className="relative max-w-[960px] overflow-hidden rounded-card border border-border bg-black leading-[0] [&>img]:pointer-events-none [&>img]:block [&>img]:h-auto [&>img]:w-full [&>img]:select-none [&>video]:pointer-events-none [&>video]:block [&>video]:h-auto [&>video]:w-full [&>video]:select-none"
        >
          {isImage ? (
            <img
              ref={imageRef}
              src={mediaUrl}
              alt=""
              draggable={false}
              onLoad={(e) =>
                adopt(
                  {
                    width: e.currentTarget.naturalWidth,
                    height: e.currentTarget.naturalHeight,
                  },
                  true,
                )
              }
              onError={() => setMediaError("Could not decode this image.")}
            />
          ) : (
            <video
              ref={videoRef}
              src={mediaUrl}
              muted
              playsInline
              preload="auto"
              onLoadedMetadata={(e) => {
                const v = e.currentTarget;
                adopt({ width: v.videoWidth, height: v.videoHeight }, false);
                if (Number.isFinite(v.duration)) {
                  setDuration(v.duration);
                } else {
                  // Streams without a duration header (e.g. some WebM/MKV
                  // exports) report Infinity until seeked past the end.
                  v.currentTime = 1e9;
                }
              }}
              onDurationChange={(e) => {
                const v = e.currentTarget;
                if (Number.isFinite(v.duration) && v.duration > 0) {
                  setDuration(v.duration);
                  if (v.currentTime > v.duration) v.currentTime = 0;
                }
              }}
              onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
              onPause={() => setPlaying(false)}
              onEnded={() => setPlaying(false)}
              onError={() =>
                setMediaError(
                  converted
                    ? `The browser could not play the converted clip. ${MANUAL_HINT}`
                    : "The browser could not decode this file.",
                )
              }
            />
          )}

          {ctx && overlay(ctx)}
        </div>
      ) : (
        <div className="rounded-card border border-dashed border-border px-[18px] py-7 text-center text-[13px] text-muted">
          {converting ? "Converting the sample with ffmpeg on the server…" : emptyHint}
        </div>
      )}

      {converted && !mediaError && (
        <p className={ui.notice}>
          Showing the first minute of {converted}, converted with ffmpeg on the
          server; the file itself was not kept there. (An NVR "MP4" that is
          really a program stream ends up here too — that is the export
          format, not a fault.)
        </p>
      )}
      {mediaError && (
        <p className={`${ui.notice} ${ui.noticeError} flex flex-wrap items-center gap-2.5`}>
          <span>{mediaError}</span>
          {sourceFile && !converted && !converting && !isImage && (
            <button
              type="button"
              className={ui.btnQuiet}
              onClick={() => void convertOnServer(sourceFile)}
              title="Send the clip to this PC's service, which converts its first minute with ffmpeg and keeps nothing"
            >
              Convert with ffmpeg on the server
            </button>
          )}
        </p>
      )}
    </div>
  );
}
